/**
 * What the two sides of the voice comparison play. A side's audio sits behind the auth-gated
 * files route and every fetch of it is one `file.read` row, so nothing is fetched when the
 * comparison opens: a side is asked for on its first play, fetched once with the operator's token
 * (`useRecordingAudio`, as a recording's audio) and played from an object URL. A side the read
 * named no audio for, or whose audio could not be fetched, cannot be played; the other still can.
 */
import { useCallback, useState } from 'react'
import type { RecordingAudioState } from '@/components/humint/questioning/recording-types'
import type {
  VoicePairSide,
  VoicePairSideId,
} from '@/components/humint/voice/match/voice-pair-types'
import { type RecordingAudio, useRecordingAudio } from '../recording/useRecordingAudio'
import type { VoiceAbSource, VoiceAbSources } from './useVoiceAb'

/** Which sides were asked for in this visit. */
export type AskedSides = Record<VoicePairSideId, boolean>

export const NOTHING_ASKED: AskedSides = { a: false, b: false }

/** `asked` with one more side; the same object when that side was asked for already. */
export function withAsked(asked: AskedSides, side: VoicePairSideId): AskedSides {
  return asked[side] ? asked : { ...asked, [side]: true }
}

/** The file to fetch for a side: none until the side was asked for, and none without audio. */
export function clipToFetch(side: Pick<VoicePairSide, 'audioUrl'>, asked: boolean): string | null {
  return asked ? side.audioUrl : null
}

const AUDIO_STATE: Record<RecordingAudio['status'], RecordingAudioState> = {
  none: 'none',
  loading: 'loading',
  ready: 'ready',
  failed: 'unavailable',
}

/** A side as the A/B transport takes it, from what the read said and how its fetch stands. */
export function clipSource(
  side: Pick<VoicePairSide, 'audioUrl' | 'audioMs'>,
  audio: RecordingAudio
): VoiceAbSource {
  if (!side.audioUrl) return { src: null, audio: 'unavailable', lengthMs: side.audioMs }
  return { src: audio.src, audio: AUDIO_STATE[audio.status], lengthMs: side.audioMs }
}

export interface VoicePairClips {
  sources: VoiceAbSources
  /** Ask for a side's audio: called by the transport on the side's first play. */
  request: (side: VoicePairSideId) => void
}

type ClipSide = Pick<VoicePairSide, 'audioUrl' | 'audioMs'>

export function useVoicePairClips(pair: { sides: readonly [ClipSide, ClipSide] }): VoicePairClips {
  const [a, b] = pair.sides
  const [asked, setAsked] = useState<AskedSides>(NOTHING_ASKED)
  const audioA = useRecordingAudio(clipToFetch(a, asked.a))
  const audioB = useRecordingAudio(clipToFetch(b, asked.b))
  const request = useCallback(
    (side: VoicePairSideId) => setAsked(current => withAsked(current, side)),
    []
  )
  return { sources: { a: clipSource(a, audioA), b: clipSource(b, audioB) }, request }
}
